import { useQuery } from '@tanstack/react-query';
import { BookOpen, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OPL_STATUSES, OPL_TYPES, OPL_TYPE_LABELS, PERMISSIONS, type OplType } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  ListRow,
  Select,
  SkeletonRows,
  StatusPill,
  oplStatusTone,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { useCan } from '../../lib/auth';
import { formatDate, humanise } from '../../lib/format';

interface OplRow {
  id: string;
  oplNo: string;
  title: string;
  oplType: string;
  status: string;
  machineCode: string | null;
  lineName: string | null;
  currentRevision: number | null;
  learningTimeMin: number | null;
  publishedAt: string | null;
}

const TYPE_TONE: Record<string, 'info' | 'warn' | 'ok'> = {
  BASIC_KNOWLEDGE: 'info',
  PROBLEM_CASE: 'warn',
  IMPROVEMENT: 'ok',
};

export function OplListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const canCreate = useCan(PERMISSIONS.OPL_CREATE);

  const filters = {
    oplType: params.get('oplType') ?? '',
    status: params.get('status') ?? '',
    machineId: params.get('machineId') ?? '',
  };

  const { data, isLoading } = useQuery({
    queryKey: ['opls', { ...filters, search }],
    queryFn: () =>
      api.get<{ data: OplRow[]; total: number }>(
        `/api/opls${qs({ ...filters, search, pageSize: 100 })}`,
      ),
  });

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="One-Point Lessons"
        subtitle="Single-page lessons that take 5–10 minutes to learn"
        actions={
          canCreate ? (
            <Button icon={Plus} onClick={() => navigate('/opl/new')}>
              New OPL
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-steel-400"
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="OPL number or title"
                aria-label="Search lessons"
                className="pl-9"
              />
            </div>
            <Select
              value={filters.oplType}
              onChange={(event) => setParam('oplType', event.target.value)}
              aria-label="Filter by type"
            >
              <option value="">Any type</option>
              {OPL_TYPES.map((value) => (
                <option key={value} value={value}>
                  {OPL_TYPE_LABELS[value]}
                </option>
              ))}
            </Select>
            <Select
              value={filters.status}
              onChange={(event) => setParam('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              {OPL_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </Select>
          </div>
        </Card>

        <Card>
          {isLoading ? (
            <SkeletonRows rows={5} />
          ) : !data?.data.length ? (
            <EmptyState
              icon={BookOpen}
              title="No lessons yet"
              message="One-Point Lessons capture what operators learn — how to do a task correctly, a problem that was solved, or an improvement worth copying."
            />
          ) : (
            <ul className="divide-y divide-steel-100">
              {data.data.map((opl) => (
                <li key={opl.id}>
                  <ListRow onClick={() => navigate(`/opl/${opl.id}`)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-x-2 text-sm font-bold text-steel-900">
                          <span className="font-mono text-info-700">{opl.oplNo}</span>
                          <span className="truncate">{opl.title}</span>
                        </p>
                        <p className="mt-0.5 text-xs text-steel-500">
                          {opl.machineCode ? `${opl.machineCode} · ` : ''}
                          {opl.lineName ?? 'All lines'}
                          {opl.learningTimeMin ? ` · ${opl.learningTimeMin} min to learn` : ''}
                          {opl.currentRevision ? ` · rev ${opl.currentRevision}` : ''}
                          {opl.publishedAt ? ` · published ${formatDate(opl.publishedAt)}` : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <Badge tone={TYPE_TONE[opl.oplType] ?? 'neutral'}>
                          {OPL_TYPE_LABELS[opl.oplType as OplType] ?? humanise(opl.oplType)}
                        </Badge>
                        <StatusPill tone={oplStatusTone(opl.status)} label={humanise(opl.status)} />
                      </div>
                    </div>
                  </ListRow>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
